import * as React from "react";
import { cn } from "../../lib/utils";

type CardSize = "large" | "medium" | "small";

interface UnifiedCardProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: CardSize;
  custom?: string;
  children: React.ReactNode;
}

const UnifiedCard = React.forwardRef<HTMLDivElement, UnifiedCardProps>(
  ({ className, size, custom, children, ...props }, ref) => {
    const getWidthClass = (): string => {
      // If custom prop has a value, use it
      if (custom && custom.trim() !== "") {
        return custom;
      }

      // Use responsive width classes that maintain desktop widths but are mobile friendly
      switch (size) {
        case "large":
          return "w-full sm:w-[90vw]";
        case "medium":
          return "w-full sm:w-[40vw]";
        case "small":
          return "w-full sm:w-[20vw]";
        default:
          return "w-full";
      }
    };

    const widthClass = getWidthClass();

    return (
      <div
        ref={ref}
        className={cn(
          "mx-auto rounded-lg sm:rounded-xl border bg-card text-card-foreground shadow-sm sm:shadow",
          "responsive-container",
          widthClass,
          // Responsive adjustments
          "max-w-full",
          // Mobile-first responsive margins and padding
          "mx-auto",
          "my-2 sm:my-4",
          // Ensure content inside is responsive and doesn't overflow
          "[&>*]:w-full [&>*]:max-w-full [&>*]:overflow-hidden",
          // Better touch targets and spacing on mobile
          "min-h-[44px]", // Minimum touch target size
          className  
        )}
        {...props}
      >
        {/* Display warning for invalid size if no custom is provided */}
        {!custom && size && !["large", "medium", "small"].includes(size) && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 py-2 rounded mb-4 text-sm">
            <strong>Warning:</strong> Invalid size "{size}" was used for the UnifiedCard component. 
            Valid sizes are: large, medium, small.
          </div>
        )}
        
        <div className="w-full">
          {children}
        </div>
      </div>
    );
  }
);

UnifiedCard.displayName = "UnifiedCard";

const UnifiedCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col space-y-1.5",
      "p-4 sm:p-6", // Smaller padding on mobile
      "pb-2 sm:pb-3", // Reduced bottom padding
      className
    )}
    {...props}
  />
));
UnifiedCardHeader.displayName = "UnifiedCardHeader";

const UnifiedCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "font-semibold leading-tight tracking-tight",
      "text-base sm:text-lg lg:text-xl", // More conservative sizing
      "line-clamp-2", // Prevent title overflow on mobile
      "break-words", // Handle long titles gracefully
      className
    )}
    {...props}
  />
));
UnifiedCardTitle.displayName = "UnifiedCardTitle";

const UnifiedCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      "text-sm text-muted-foreground leading-relaxed",
      "sm:text-base", // Less aggressive scaling
      "line-clamp-3", // Limit to 3 lines on mobile
      "break-words", // Handle long words
      className
    )}
    {...props}
  />
));
UnifiedCardDescription.displayName = "UnifiedCardDescription";

const UnifiedCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "p-4 sm:p-6", // Responsive padding
      "pt-0", // No top padding to connect with header
      "w-full",
      // Ensure all children are responsive and handle overflow
      "[&>*]:max-w-full [&>*]:overflow-hidden [&>*]:break-words",
      // Better spacing for mobile
      "space-y-3 sm:space-y-4",
      className
    )}
    {...props}
  />
));
UnifiedCardContent.displayName = "UnifiedCardContent";

const UnifiedCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center gap-2 sm:gap-3",
      "p-4 sm:p-6", // Responsive padding
      "pt-0", // No top padding
      "flex-wrap", // Allow wrapping on small screens
      "justify-start sm:justify-end", // Left align on mobile, right on desktop
      className
    )}
    {...props}
  />
));
UnifiedCardFooter.displayName = "UnifiedCardFooter";

export {
  UnifiedCard,
  UnifiedCardHeader,
  UnifiedCardFooter,
  UnifiedCardTitle,
  UnifiedCardDescription,
  UnifiedCardContent,
};